import { useEffect, useMemo, useState } from "react";
import {
  Section,
  Row,
  StatCard,
  HeroCard,
  Bullet,
  toTitle,
  secondsToMin,
  pct,
  money,
  fmtNumber,
} from "./ReportUIPrimitives";
import styles from "./BenchmarkViewer.module.css";

const BenchmarkViewer = ({ data = {}, view = null }) => {
  const {
    summary,
    month,
    window: reportWindow,
    default_window_key: defaultWindowKey = null,
    available_windows: availableWindows = [],
    windows = [],
    date_brackets,
    shopify_sales,
    orders: rootOrders,
    traffic_analytics,
    customers: rootCustomers,
    facebook_ads,
    product_imagery_benchmarks,
    web_branding_benchmark,
    homepage_image_assessment,
    flags,
  } = data;

  // console.log("[BenchmarkViewer] window data", {
  //   reportWindow,
  //   date_brackets: date_brackets ?? "(missing)",
  //   start_date: date_brackets?.start_date ?? "(missing)",
  //   end_date:   date_brackets?.end_date   ?? "(missing)",
  // });

  const ga = traffic_analytics?.totals || {};
  const wb = web_branding_benchmark || {};
  const schemaQuality = wb.schema_quality || {};
  const schemaStructure = wb.schema_structure || {};
  const schemaRecommendation = wb.schema_recommendation || {};
  const homepageAssessment = homepage_image_assessment || {};
  const [expandedJsonSections, setExpandedJsonSections] = useState({});

  const initialWindowList = useMemo(() => {
    if (Array.isArray(availableWindows) && availableWindows.length) {
      return availableWindows.map((windowEntry) => ({
        key: windowEntry?.key || "",
        days: windowEntry?.days || null,
        startDate: windowEntry?.startDate || null,
        endDate: windowEntry?.endDate || null,
        label: windowEntry?.label || "",
      }));
    }
    if (Array.isArray(windows) && windows.length) {
      return windows.map((windowEntry) => ({
        key: windowEntry?.key || "",
        days: windowEntry?.days || null,
        startDate:
          windowEntry?.startDate || windowEntry?.reporting_period?.startDate || null,
        endDate:
          windowEntry?.endDate || windowEntry?.reporting_period?.endDate || null,
        label: windowEntry?.label || windowEntry?.reporting_period?.label || "",
      }));
    }
    return [];
  }, [availableWindows, windows]);

  const [activeWindowKey, setActiveWindowKey] = useState(
    defaultWindowKey || initialWindowList[0]?.key || null
  );

  useEffect(() => {
    setActiveWindowKey(defaultWindowKey || initialWindowList[0]?.key || null);
  }, [defaultWindowKey, initialWindowList]);

  const activeWindowData = useMemo(() => {
    if (!Array.isArray(windows) || !windows.length) return null;
    return (
      windows.find((windowEntry) => windowEntry?.key === activeWindowKey) ||
      windows.find((windowEntry) => windowEntry?.key === defaultWindowKey) ||
      windows[0] ||
      null
    );
  }, [windows, activeWindowKey, defaultWindowKey]);

  const orders = activeWindowData?.orders || rootOrders || {};
  const customers = activeWindowData?.customers || rootCustomers || {};
  const activeDateBrackets =
    activeWindowData?.startDate || activeWindowData?.endDate
      ? {
          start_date: activeWindowData?.startDate || null,
          end_date: activeWindowData?.endDate || null,
        }
      : date_brackets || {};
  const activeSummary = activeWindowData?.summary || summary || "";
  const orderCurrency =
    orders?.order_revenue_shop_currency ||
    orders?.net_order_revenue_after_discounts_shop_currency ||
    "USD";

  const schemaQualityEntries = [
    {
      label: "Organization Schema",
      value: schemaQuality.schema_org_quality,
    },
    {
      label: "Web or Online Store Schema",
      value: schemaQuality.schema_web_quality,
    },
  ];

  const listOrNone = (items) =>
    Array.isArray(items) && items.length ? items.join(", ") : "None";

  const formatDecimal = (value, digits = 2) => {
    if (value == null) return "—";
    const num = typeof value === "number" ? value : parseFloat(value);
    if (Number.isNaN(num)) return "—";
    return num.toFixed(digits);
  };

  const schemaStructureSections = [
    {
      key: "organization",
      title: "Organization Schema Structure",
      data: schemaStructure.organization,
    },
    {
      key: "online_store",
      title: "Online Store Schema Structure",
      data: schemaStructure.online_store,
    },
  ];
  const activeStructureSections = schemaStructureSections.filter((section) =>
    Boolean(section.data),
  );

  const jsonRecommendationEntries = [
    {
      key: "organization",
      label: "Organization Schema",
      content: schemaRecommendation.organization_schema,
    },
    {
      key: "online_store",
      label: "Online Store Schema",
      content: schemaRecommendation.online_store_schema,
    },
  ];
  const hasJsonRecommendations = jsonRecommendationEntries.some((entry) =>
    Boolean(entry.content),
  );
  const toggleJsonSection = (key) =>
    setExpandedJsonSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));

  const renderJsonPreview = (jsonString, lines = 6) => {
    const lineArray = jsonString.split("\n");
    if (lineArray.length <= lines) return jsonString;
    return `${lineArray.slice(0, lines).join("\n")}\n…`;
  };

  const getQualityAccent = (value) => {
    if (!value) return "#475569";
    const normalized = value.toLowerCase();
    if (normalized.includes("excellent") || normalized.includes("strong")) {
      return "#22C55E";
    }
    if (normalized.includes("good")) {
      return "#14B8A6";
    }
    if (normalized.includes("fair") || normalized.includes("average")) {
      return "#F59E0B";
    }
    if (normalized.includes("poor") || normalized.includes("weak")) {
      return "#EF4444";
    }
    return "#60A5FA";
  };

  const gaWindow = traffic_analytics?.window || reportWindow || {};
  const trafficWindowLabel = gaWindow?.start
    ? `${new Date(gaWindow.start).toLocaleDateString()} → ${
        gaWindow?.end ? new Date(gaWindow.end).toLocaleDateString() : "—"
      }`
    : "—";
  const trafficWindowDays = gaWindow?.days ? `${gaWindow.days} days` : "—";
  const trafficWindowSource = gaWindow?.source || "GA4";

  const trafficHighlights = [
    { label: "Active Users", value: ga.activeUsers },
    { label: "Screen/Page Views", value: ga.screenPageViews },
    { label: "Engaged Sessions", value: ga.engagedSessions },
  ];

  const trafficConversionCards = [
    // { label: "Conversions", value: fmtNumber(ga.conversions) },
    // { label: "Transactions", value: fmtNumber(ga.transactions) },
    // { label: "Total Revenue", value: money(ga.totalRevenue, "USD") },
    // { label: "Purchase Revenue", value: money(ga.purchaseRevenue, "USD") },
  ];

  const trafficDetailRows = [
    {
      label: "Avg Session Duration",
      value: secondsToMin(ga.averageSessionDurationSec),
    },
    { label: "Bounce Rate", value: pct(ga.bounceRate) },
    { label: "Conversion Rate", value: pct(ga.conversionRate) },
    { label: "Sessions (approx)", value: fmtNumber(ga.sessionsApprox) },
    { label: "New Users", value: fmtNumber(ga.newUsers) },
  ];

  const totalCustomers = customers?.total_customers;
  const newCustomers = customers?.new_customers;
  const returningCustomers = customers?.returning_customers;
  const repeatPurchaseRate = customers?.repeat_purchase_rate;
  const averageOrdersPerCustomer = customers?.average_orders_per_customer;
  const topCustomerRevenueShare =
    customers?.top_10_percent_customer_revenue_share;

  const repeatPct =
    repeatPurchaseRate != null
      ? Math.min(Math.max(repeatPurchaseRate * 100, 0), 100)
      : 0;

  const newCustomerShare =
    totalCustomers != null && newCustomers != null && totalCustomers > 0
      ? (newCustomers / totalCustomers) * 100
      : null;

  const homepageAssessments = Array.isArray(homepageAssessment.hero_assessments)
    ? homepageAssessment.hero_assessments
    : [];

  const productImagery = Array.isArray(product_imagery_benchmarks)
    ? product_imagery_benchmarks
    : [];



  return (
    <div className={styles.container}>
      <>
          {(initialWindowList.length > 0 || activeDateBrackets?.start_date || activeDateBrackets?.end_date) && (
            <div className={styles.windowBar}>
              {initialWindowList.length ? (
                <select
                  className={styles.windowPickerSelect}
                  value={activeWindowKey || ""}
                  onChange={(e) => setActiveWindowKey(e.target.value)}
                  aria-label="Benchmark report time window"
                >
                  {initialWindowList.map((windowEntry) => (
                    <option key={windowEntry.key} value={windowEntry.key}>
                      {windowEntry.label}
                    </option>
                  ))}
                </select>
              ) : null}
              {activeDateBrackets.start_date && (
                <span className={styles.windowDate}>
                  {new Date(activeDateBrackets.start_date).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    },
                  )}
                </span>
              )}
              {activeDateBrackets.start_date && activeDateBrackets.end_date && (
                <span className={styles.windowSep}>→</span>
              )}
              {activeDateBrackets.end_date && (
                <span className={styles.windowDate}>
                  {new Date(activeDateBrackets.end_date).toLocaleDateString(
                    "en-US",
                    {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    },
                  )}
                </span>
              )}
            </div>
          )}
          {activeSummary ? (
            <div className={styles.summaryCard}>
              <p className={styles.summaryLabel}>Report Summary</p>
              <p className={styles.summaryText}>{activeSummary}</p>
            </div>
          ) : null}
          <HeroCard title="Order Summary" badge={month}>
            <div className={styles.statsGrid}>
              <StatCard
                label="Orders"
                value={fmtNumber(orders?.orders_count)}
              />
              <StatCard
                label="Items Ordered"
                value={fmtNumber(orders?.items_ordered)}
              />
              <StatCard
                label="Returns"
                value={fmtNumber(orders?.returns_count)}
              />
              <StatCard
                label="Orders Fulfilled"
                value={fmtNumber(orders?.orders_fulfilled)}
              />
              <StatCard
                label="Orders Delivered"
                value={fmtNumber(orders?.orders_delivered)}
              />
              <StatCard
                label="Order Revenue"
                value={money(orders?.order_revenue_shop_money, orderCurrency)}
              />
              <StatCard
                label="Net Revenue (After Discounts)"
                value={money(
                  orders?.net_order_revenue_after_discounts_shop_money,
                  orderCurrency,
                )}
              />
            </div>
          </HeroCard>
          <Section title="Customers">
            <div className={styles.customerHeader}>
              <div>
                <h5 className={styles.customerTitle}>Customer Momentum</h5>
                <p className={styles.customerSubtitle}>
                  {totalCustomers != null && newCustomers != null
                    ? `${fmtNumber(newCustomers)} of ${fmtNumber(totalCustomers)} customers were new${
                        newCustomerShare != null
                          ? ` (${newCustomerShare.toFixed(1)}%)`
                          : ""
                      }`
                    : "Insight pending"}
                </p>
              </div>
            </div>
            <div className={styles.customerRow}>
              <div className={styles.repeatMeter}>
                <span className={styles.repeatLabel}>Repeat Purchase Rate</span>
                <span className={styles.repeatValue}>
                  {pct(repeatPurchaseRate)}
                </span>
                <div className={styles.repeatBar}>
                  <div
                    className={styles.repeatFill}
                    style={{ width: `${repeatPct}%` }}
                  />
                </div>
              </div>
              <div className={styles.statsRow}>
                <StatCard
                  label="Total Customers"
                  value={fmtNumber(totalCustomers)}
                  accent="#22d3ee"
                />
                <StatCard
                  label="New Customers"
                  value={fmtNumber(newCustomers)}
                  accent="#38bdf8"
                />
                <StatCard
                  label="Returning Customers"
                  value={fmtNumber(returningCustomers)}
                  accent="#7c3aed"
                />
                <StatCard
                  label="Avg Orders / Customer"
                  value={formatDecimal(averageOrdersPerCustomer, 2)}
                  accent="#10b981"
                />
                <StatCard
                  label="Top 10% Revenue Share"
                  value={pct(topCustomerRevenueShare)}
                  accent="#f97316"
                />
              </div>
            </div>
            <div className={styles.detailList}>
              <Row
                label="Average Orders per Customer"
                value={formatDecimal(averageOrdersPerCustomer, 2)}
              />
              <Row
                label="Top 10% Revenue Share"
                value={pct(topCustomerRevenueShare)}
              />
            </div>
          </Section>
      </>
    </div>
  );
};

export default BenchmarkViewer;
